import { useMemo, useState } from 'react';
import { Box, Button, IconButton, Alert } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';
import type { HourlyRate } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot, orderByField } from '../../core/data/useCollectionSnapshot';
import { createHourlyRate, updateHourlyRate, deleteHourlyRate } from '../../core/data/adminMutations';
import HourlyRateDialog, { type ExistingHourlyRate } from './HourlyRateDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { adminDataGridSx } from './adminDataGridSx';
import { formatCurrencyEUR } from '../projects/finance';

export default function HourlyRatesTab() {
  const { t, i18n } = useTranslation();
  const { rows, loading, error } = useCollectionSnapshot<HourlyRate>(
    'hourlyRates',
    orderByField('label'),
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExistingHourlyRate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExistingHourlyRate | null>(null);
  const [busy, setBusy] = useState(false);

  const gridRows = useMemo(
    () =>
      rows.map(({ id, data }) => ({
        id,
        label: data.label,
        amount: data.amount,
      })),
    [rows],
  );
  const allForUniqueness = useMemo(
    () => rows.map(({ id, data }) => ({ id, label: data.label })),
    [rows],
  );

  const onCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const onEditRow = (row: (typeof gridRows)[number]) => {
    setEditing({ id: row.id, label: row.label, amount: row.amount });
    setDialogOpen(true);
  };

  const handleSubmit = async (values: { label: string; amount: number }) => {
    if (editing) {
      await updateHourlyRate(editing.id, values);
    } else {
      await createHourlyRate(values);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteHourlyRate(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'label', headerName: t('admin.hourlyRates.label'), flex: 1, minWidth: 200 },
    {
      field: 'amount',
      headerName: t('admin.hourlyRates.amount'),
      width: 200,
      renderCell: (params) => formatCurrencyEUR(params.value as number, i18n.language),
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label={t('common.edit')}
            onClick={() => onEditRow(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('common.delete')}
            onClick={() =>
              setDeleteTarget({ id: params.row.id, label: params.row.label, amount: params.row.amount })
            }
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={onCreate}>
          {t('admin.hourlyRates.new')}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={{ height: 520 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnSelector
          density="standard"
          sx={adminDataGridSx}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>

      <HourlyRateDialog
        open={dialogOpen}
        editing={editing}
        allRates={allForUniqueness}
        onSubmit={handleSubmit}
        onClose={() => setDialogOpen(false)}
      />
      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title={t('admin.hourlyRates.deleteTitle')}
        body={t('admin.hourlyRates.deleteBody', { label: deleteTarget?.label ?? '' })}
        busy={busy}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
